import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useHistory, useLocation, withRouter } from "react-router-dom";
import axios from "axios";
import moment from "moment";
import { API_URL } from "src/scenes/App";

import { default as NoticeboardTable } from "./components/NoticeboardTable";
import DeleteDialog from "./components/DeleteDialog";
import NoticeModal from "./components/NoticeModal";
import NoticeboardFiltersModal, {
    EMPTY_NOTICE_FILTERS,
    getActiveNoticeFilterCount
} from "./components/NoticeboardFiltersModal";
import NoticeViewershipModal from "./components/NoticeViewershipModal";
import pluralize from "pluralize";
import { capitalizeString, customNames } from "src/utils/helpers";
import {
    BusinessPortalHeader,
    PortalCaption,
    PortalHighlightMetricCard,
    PortalMetricCard,
    PortalMetricGrid,
    PortalMetricLabel,
    PortalMetricValue,
    PortalPageShell,
    PortalSurface,
    Toast,
} from "src/components";
import {
    NoticeboardViewCard,
    NoticeboardViewCardButton,
    NoticeboardViewCardText,
    NoticeboardViewCardTitle
} from "src/components/NoticeboardFeedUi";
import NoticeboardAccountLinkingCard from "./components/NoticeboardAccountLinkingBanner";
import AccountLinkingPage from "./components/AccountLinking/AccountLinkingPage";
import { FinaliseLinkedInModal } from "./components/AccountLinking/FinaliseLinkedInModal";
import {
    CLIENT_NOTICEBOARD,
    NOTICEBOARD,
    SOCIAL_MEDIA_LINKING_POSTING,
    SOCIAL_MEDIA_POSTING
} from "src/constants/permissions";
import useAccountLinks from "./components/AccountLinking/hooks/useAccountLinks";
import FacebookSDKLoader from "./components/FacebookSDKLoader";
import { POST_TYPE } from "./constants";
import { NoticeVideo } from "./mediaAssets";
import NoticeboardFeedView from "./components/feed/NoticeboardFeedView";
import { FEATURE_FLAGS, hasFeature } from "src/constants/FeatureFlag";

export const FILTER_NOTICE = {
    EMPLOYEE: "employee",
    CLIENT: "client"
};

export const FILTER_BY = {
    ALL_NOTICES: "All",
    ACTIVE_POSTS: "Active Posts",
    SCHEDULED_POSTS: "Scheduled Posts"
};

const NOTICEBOARD_API_FILTER = {
    [FILTER_BY.ALL_NOTICES]: "all",
    [FILTER_BY.ACTIVE_POSTS]: "active",
    [FILTER_BY.SCHEDULED_POSTS]: "scheduled"
};

const DEFAULT_RESPONSE_MESSAGE = {
    SUBJECT_INVALID: { "input": "subjectInvalid", "message": "" },
    MESSAGE_INVALID: { "input": "messageInvalid", "message": "" },
    SCHEDULE_INVALID: { "input": "scheduleDateInvalid", "message": "" }
};

const NOTICEBOARD_VIEW = {
    MANAGE: "manage",
    FEED: "feed"
};

const checkedInAwareNoticeFilters = (filters, canUseCheckedIn) => ({
    ...filters,
    checkInCategories: canUseCheckedIn ? filters.checkInCategories : []
});

const Noticeboard = ({ user }) => {

    const location = useLocation();
    const history = useHistory();
    const query = new URLSearchParams(location.search);
    const connectType = query.get("connectType");
    const code = query.get("code");
    const isFinalisingLinkedIn = connectType === "linkedin" && Boolean(code);
    const isClientUser = Boolean(user.isClient);
    const canManageEmployees = user.permissions.includes(NOTICEBOARD);
    const canManageClients = user.permissions.includes(CLIENT_NOTICEBOARD);
    const canManageNoticeboard = !isClientUser && (canManageEmployees || canManageClients);
    const canCreateClientPost = isClientUser && canManageClients;
    const canUseCheckedIn = hasFeature(user, FEATURE_FLAGS.CHECKED_IN);

    const {
        retrieveLinkedAccounts,
        queryAvailableLinkedInOrganisations,
        saveLinkedInDetails,
        dismissLinkedInLinking,
        linkedAccounts,
        linkedAccountsFetched,
        connectingLinkedInAccount,
        setConnectingLinkedInAccount,
        saveFacebookDetails,
        saveInstagramDetails,
        revokeSocialMediaAccount,
        handleFacebookReconnect,
        handleInstagramReconnect,
        showAccountsLinked
    } = useAccountLinks(user.token, isFinalisingLinkedIn);

    const [filterOption, setFilterOption] = useState(
        !isClientUser && canManageEmployees ? FILTER_NOTICE.EMPLOYEE : FILTER_NOTICE.CLIENT
    );
    const [isFetching, setIsFetching] = useState(false);
    const [employeesArray, setEmployeesArray] = useState([]);
    const [clientsArray, setClientsArray] = useState([]);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [noticeUuidsForDeletion, setNoticeUuidsForDeletion] = useState([]);
    const [showAccountLinkingPage, setShowAccountLinkingPage] = useState(false);
    const [isRequestingDeletion, setIsRequestingDeletion] = useState(false);
    const [checkAllNotices, setCheckAllNotices] = useState(false);
    const [checkedNotices, setCheckedNotices] = useState([]);
    const [showNoticeModal, setShowNoticeModal] = useState(false);
    const [showNoticeViewershipModal, setShowNoticeViewershipModal] = useState(false);
    const [noticeData, setNoticeData] = useState(null);
    const [isFormLoading, setIsFormLoading] = useState(false);
    const [responseMessage, setResponseMessage] = useState(DEFAULT_RESPONSE_MESSAGE);
    const [filterByOption, setFilterByOption] = useState(FILTER_BY.ALL_NOTICES);
    const [appliedNoticeFilters, setAppliedNoticeFilters] = useState(EMPTY_NOTICE_FILTERS);
    const [draftNoticeFilters, setDraftNoticeFilters] = useState(EMPTY_NOTICE_FILTERS);
    const [showNoticeFilters, setShowNoticeFilters] = useState(false);
    const [feedRefreshKey, setFeedRefreshKey] = useState(0);
    const [activeView, setActiveView] = useState(
        canManageNoticeboard ? NOTICEBOARD_VIEW.MANAGE : NOTICEBOARD_VIEW.FEED
    );
    const hasSocialMediaLinkingRole = user.permissions.includes(SOCIAL_MEDIA_LINKING_POSTING);
    const hasSocialMediaPostingRole = (
        user.permissions.includes(SOCIAL_MEDIA_POSTING) || user.permissions.includes(SOCIAL_MEDIA_LINKING_POSTING)
    );

    const updateMessage = (messageType, newMessage) => {
        setResponseMessage((prevState) => ({
            ...prevState,
            [messageType]: {
                ...prevState[messageType],
                message: newMessage,
            },
        }));
    };

    useEffect(() => {
        if (isFinalisingLinkedIn && canManageNoticeboard) {
            setShowAccountLinkingPage(true);
            setConnectingLinkedInAccount(true);
            setActiveView(NOTICEBOARD_VIEW.MANAGE);
        }
    }, [canManageNoticeboard, code, connectType, isFinalisingLinkedIn, setConnectingLinkedInAccount]);

    useEffect(() => {
        if (!canManageNoticeboard) {
            setActiveView(NOTICEBOARD_VIEW.FEED);
            setShowAccountLinkingPage(false);
        }
    }, [canManageNoticeboard]);

    useEffect(() => {
        const noticeboardFilter = new URLSearchParams(location.search).get("noticeboard");
        if (![FILTER_NOTICE.EMPLOYEE, FILTER_NOTICE.CLIENT].includes(noticeboardFilter)) {
            return;
        }
        if (!canManageNoticeboard) {
            history.replace("/noticeboard");
            return;
        }

        resetListingState();
        setFilterOption(noticeboardFilter);
        setActiveView(NOTICEBOARD_VIEW.MANAGE);
        history.replace("/noticeboard");
    }, [canManageNoticeboard, history, location.search]);

    useEffect(() => {
        if (!canManageNoticeboard || activeView !== NOTICEBOARD_VIEW.MANAGE) {
            return;
        }
        if (canManageEmployees) {
            fetchData(FILTER_NOTICE.EMPLOYEE);
        }
        if (canManageClients) {
            fetchData(FILTER_NOTICE.CLIENT);
        }
    }, [
        activeView,
        canManageClients,
        canManageEmployees,
        canManageNoticeboard,
        canUseCheckedIn,
        appliedNoticeFilters,
        filterByOption,
        user.token
    ]);

    useEffect(() => {
        if (canManageNoticeboard && hasSocialMediaPostingRole) {
            retrieveLinkedAccounts();
        }
    }, [canManageNoticeboard, hasSocialMediaPostingRole, user.token]);

    useEffect(() => {
        if (canUseCheckedIn) {
            return;
        }

        setAppliedNoticeFilters(current => checkedInAwareNoticeFilters(current, false));
        setDraftNoticeFilters(current => checkedInAwareNoticeFilters(current, false));
    }, [canUseCheckedIn]);


    function setUsersArray(userFilterOption, responseData) {
        if (userFilterOption === FILTER_NOTICE.EMPLOYEE) {
            setEmployeesArray(responseData);
        } else {
            setClientsArray(responseData);
        }
    }

    function getUsers() {
        let unsortedUserArray = [];
        if (filterOption === FILTER_NOTICE.EMPLOYEE) {
            unsortedUserArray = employeesArray;
        } else {
            unsortedUserArray = clientsArray;
        }

        if (!unsortedUserArray)
            return [];

        return sortByDate(unsortedUserArray);
    }

    const sortByDate = (unsortedUserArray) => {
        return unsortedUserArray.sort((a, b) => {
            const getEffectiveDate = (item) => new Date(item.postedDate || item.scheduleDate || item.created_at);

            const dateA = getEffectiveDate(a);
            const dateB = getEffectiveDate(b);

            return dateB - dateA;
        });
    };

    let usersArray = filteredUsers();

    function filteredUsers() {
        const unfilteredUserArray = getUsers();

        const filteredUserArray = unfilteredUserArray.filter(notice => {
            switch (filterByOption) {
                case FILTER_BY.SCHEDULED_POSTS:
                    return !notice.isActive;

                case FILTER_BY.ACTIVE_POSTS:
                    return notice.isActive;

                default:
                    return true;
            }
        });

        return filteredUserArray;
    }

    const fetchData = async (userFilterOption) => {
        const noticeboardApi = `${API_URL}/noticeboard/notices/${userFilterOption === FILTER_NOTICE.EMPLOYEE ? "employees" : "clients"}`;
        const filter = NOTICEBOARD_API_FILTER[filterByOption];
        const params = {
            filter,
            search: appliedNoticeFilters.search || undefined,
            checkInCategories: canUseCheckedIn && appliedNoticeFilters.checkInCategories.length ?
                appliedNoticeFilters.checkInCategories :
                undefined,
            myPostsOnly: appliedNoticeFilters.myPostsOnly || undefined,
            month: appliedNoticeFilters.month || undefined,
            year: appliedNoticeFilters.year || undefined
        };

        setIsFetching(true);
        try {
            const res = await axios.get(noticeboardApi, {
                params,
                headers: {
                    Authorization: "Bearer " + user.token
                }
            });

            const notices = getRequiredResponseField(res, "notices");
            setUsersArray(userFilterOption, notices);
        } catch (error) {
            console.log(error)
            // Handle error
        } finally {
            setIsFetching(false);
        }
    };

    const handleFilterChangeByUserType = value => {
        resetListingState();
        setFilterOption(value);
    };

    const handleFilterByChange = event => {
        resetListingState();
        setFilterByOption(event.target.value);
    };

    const handleOpenNoticeFilters = () => {
        setDraftNoticeFilters(checkedInAwareNoticeFilters(appliedNoticeFilters, canUseCheckedIn));
        setShowNoticeFilters(true);
    };

    const handleApplyNoticeFilters = () => {
        resetListingState();
        setAppliedNoticeFilters({
            ...checkedInAwareNoticeFilters(draftNoticeFilters, canUseCheckedIn),
            search: draftNoticeFilters.search.trim()
        });
        setShowNoticeFilters(false);
    };

    const handleClearNoticeFilters = () => {
        resetListingState();
        setDraftNoticeFilters(EMPTY_NOTICE_FILTERS);
        setAppliedNoticeFilters(EMPTY_NOTICE_FILTERS);
        setShowNoticeFilters(false);
    };

    const toggleDraftNoticeCategory = (categoryId) => {
        setDraftNoticeFilters(current => {
            const selected = current.checkInCategories.includes(categoryId);
            return {
                ...current,
                checkInCategories: selected ?
                    current.checkInCategories.filter(id => id !== categoryId) :
                    [...current.checkInCategories, categoryId]
            };
        });
    };

    const handleChangedFilterByUserTypeAndScheduleStatus = (userType, isActive) => {
        resetListingState();
        setFilterOption(userType);

        if (filterByOption !== FILTER_BY.ALL_NOTICES) {
            const filterBy = isActive ? FILTER_BY.ACTIVE_POSTS : FILTER_BY.SCHEDULED_POSTS;
            setFilterByOption(filterBy);
        }
    };

    const resetListingState = () => {
        setCheckAllNotices(false);
        setCheckedNotices([]);
    };

    /**
     * @param {array} noticeUuids
     * @returns {Promise<void>}
     */
    const handleMultipleDelete = async (noticeUuids) => {
        try {
            setIsRequestingDeletion(true);
            await Promise.all(noticeUuids.map(noticeUuid => (
                axios.delete(`${API_URL}/noticeboard/${noticeUuid}`, {
                    headers: {
                        Authorization: "Bearer " + user.token
                    }
                })
            )));

            const message = noticeUuids.length === 1 ?
                "Selected post has been deleted" :
                "Selected posts have been deleted";
            Toast.success(message);
        } catch (error) {
            console.log(error)
            Toast.error("Unable to process deletion of noticeboard");
        } finally {
            setShowNoticeModal(false);
            setIsRequestingDeletion(false);

            // Re-fetching both Noticeboards in case some notices were posted to both of them
            fetchData(FILTER_NOTICE.EMPLOYEE);
            fetchData(FILTER_NOTICE.CLIENT);

            setCheckedNotices([]);
            handleCloseDeleteDialog();
        }
    };

    const handleCheckAllNotices = (notices) => {
        const toggleState = !checkAllNotices;
        setCheckAllNotices(toggleState);
        const newCheckedUuids = toggleState ? notices.map(notice => notice.uuid) : [];
        setCheckedNotices(newCheckedUuids);
    };

    const handleCheckboxChange = (noticeUuid) => {
        let newCheckedUuids = [];

        if (!checkedNotices.includes(noticeUuid)) {
            newCheckedUuids = [...checkedNotices, noticeUuid];
        } else {
            newCheckedUuids = checkedNotices.filter(uuid => uuid !== noticeUuid);
        }

        setCheckedNotices(newCheckedUuids);

        //uncheck all/check all
        if (newCheckedUuids.length === getUsers().length) {
            setCheckAllNotices(true);
        } else {
            setCheckAllNotices(false);
        }
    };

    const handleDelete = () => {
        handleMultipleDelete(noticeUuidsForDeletion);
    };

    const requestDeleteNoticesConfirmation = noticeUuids => {
        if (setNoticeUuidsForDeletion.length === 0) {
            return;
        }

        setNoticeUuidsForDeletion(noticeUuids);
        setShowDeleteDialog(true);
    };

    const handleCloseDeleteDialog = () => {
        setShowDeleteDialog(false);
        setNoticeUuidsForDeletion([]);
    };

    const handleEditNotice = notice => {
        setNoticeData(notice);
        setShowNoticeModal(true);
    };

    const handleNewNotice = () => {
        setNoticeData(null);
        setShowNoticeModal(true);
    };

    const handleCloseNoticeModal = () => {
        setShowNoticeModal(false);
    };

    const handleOpenLinkedAccountsFromNoticeModal = () => {
        setShowNoticeModal(false);
        setShowAccountLinkingPage(true);
    };

    const handleShowNoticeViewershipModal = notice => {
        setNoticeData(notice);
        setShowNoticeViewershipModal(true);
    };

    const formatDateToYmdTHi = (dateString) => {
        if (!dateString) {
            return null;
        }

        const momentDate = moment(dateString);
        if (!momentDate.isValid()) {
            return null;
        }

        return momentDate.format("YYYY-MM-DDTHH:mm");
    };

    const getSingleReaderType = (readerTypes) => {
        if (!readerTypes.length) {
            throw new Error("Preferred Noticeboard endpoints require at least one reader type.");
        }

        return readerTypes[0];
    };

    const requireReaderTypes = (readerTypes) => {
        if (!readerTypes.length) {
            throw new Error("Preferred Noticeboard endpoints require at least one reader type.");
        }
    };

    const getMediaExtension = (noticeMediaItem) => {
        const file = Array.isArray(noticeMediaItem) ?
            noticeMediaItem[0]?.pendingLocalUpload?.() :
            noticeMediaItem?.pendingLocalUpload?.();

        if (!file?.type) {
            throw new Error("A media file type is required for Noticeboard media uploads.");
        }

        return file.type.split("/").pop();
    };

    const buildAppNoticeRequest = (formRequest, noticeMediaItem) => {
        const request = {
            postType: formRequest.postType
        };
        const optionalFields = [
            "subject",
            "message",
            "richTextMessage",
            "allowComments",
            "isPostNow",
            "mediaHasChanged",
            "selectedSocialsToPostUuids",
            "readerTypes",
            "carouselMedia",
            "checkIn"
        ];

        optionalFields.forEach(field => {
            if (formRequest[field] !== undefined) {
                request[field] = formRequest[field];
            }
        });

        const scheduleDate = formatDateToYmdTHi(formRequest.scheduleDate);
        if (scheduleDate) {
            request.scheduleDate = scheduleDate;
        }

        const needsMediaExtension = (
            formRequest.postType !== POST_TYPE.TEXT &&
            formRequest.postType !== POST_TYPE.CAROUSEL &&
            (noticeData === null || formRequest.mediaHasChanged)
        );
        if (needsMediaExtension) {
            request.mediaExtension = getMediaExtension(noticeMediaItem);
        }

        return request;
    };

    const getRequiredResponseField = (response, field) => {
        if (!response.data || response.data[field] === undefined) {
            throw new Error(`Noticeboard API response missing required '${field}' field.`);
        }

        return response.data[field];
    };

    const removeUndefinedFields = (object) => {
        return Object.fromEntries(
            Object.entries(object).filter(([, value]) => value !== undefined)
        );
    };

    const handleCloseNoticeViewershipModal = () => {
        setShowNoticeViewershipModal(false);
    };

    /** save noticeboard first, then start uploading the files */
    const uploadAttachedFileToS3AndSave = async (noticeUuid, attachedFile) => {
        const responseOfAttachment = await axios.get(`${API_URL}/noticeboard/attachmentsS3PresignedUrl/${noticeUuid}/${attachedFile.name}`, {
            headers: {
                Authorization: "Bearer " + user.token
            }
        });

        const attachmentS3PresignedUrl = responseOfAttachment.data.attachmentS3PresignedUrl;
        const storedUrl = await uploadAttachmentToS3(attachmentS3PresignedUrl, attachedFile.preview, attachedFile.type);
        const formRequest = {
            filename: attachedFile.name,
            s3Url: storedUrl,
        };

        await attachmentCreate(noticeUuid, [formRequest]);
    };

    /** save noticeboard first, then start deleting the files */
    const deleteAttachedFile = async (noticeUuid, deletedFile) => {
        await axios.delete(`${API_URL}/noticeboard/attachment/${noticeUuid}/${deletedFile.attachmentUuid}`, {
            headers: {
                Authorization: "Bearer " + user.token
            }
        });
    };

    /**
     * @param formData
     * @param {NoticeImage[]|NoticeVideo[]|undefined} noticeMediaItem
     * @param modalSetErrors
     * @param attachedFiles
     * @param deleteAttachedFiles
     */
    const createOrUpdateNotice = async (formData, noticeMediaItem, modalSetErrors, attachedFiles, deleteAttachedFiles) => {
        const readerTypes = canCreateClientPost ?
            [FILTER_NOTICE.CLIENT] :
            [];
        if (!canCreateClientPost) {
            if (formData.visibleToEmployees) {
                readerTypes.push(FILTER_NOTICE.EMPLOYEE);
            }
            if (formData.visibleToClients) {
                readerTypes.push(FILTER_NOTICE.CLIENT);
            }
        }
        formData.readerTypes = readerTypes;

        try {
            setResponseMessage(DEFAULT_RESPONSE_MESSAGE);
            setIsFormLoading(true);

            const notice = noticeData === null ?
                await createNotice(formData, noticeMediaItem, modalSetErrors, attachedFiles, deleteAttachedFiles) :
                await updateNotice(formData, noticeMediaItem, modalSetErrors, attachedFiles, deleteAttachedFiles);

            // Update social media postings
            if (hasSocialMediaLinkingRole || hasSocialMediaPostingRole) {
                const hasLinkedAccountsToPostTo = (formData.selectedSocialsToPostUuids && formData.selectedSocialsToPostUuids.length > 0);
                const hasLinkedAccountsToRemovePostFrom = (formData.socialsToRemove && formData.socialsToRemove.length > 0);
                if (hasLinkedAccountsToPostTo || hasLinkedAccountsToRemovePostFrom) {
                    const response = hasLinkedAccountsToRemovePostFrom ?
                        await updatePostToSocialPlatform(notice.uuid, formData.selectedSocialsToPostUuids, formData.socialsToRemove) :
                        await postToSocialPlatform(notice.uuid, formData.selectedSocialsToPostUuids);
                    if (response.errors && response.errors.length > 1) {
                        const warningMessage = response.errors.flat().join(",");
                        Toast.error(warningMessage);
                    }
                }
            }

            if (canManageNoticeboard) {
                // Change the active filter if the new notice doesn't belong in the current one
                const isNoticeActive = notice.isActive;
                const newFilterOption = formData.readerTypes.includes(filterOption) ?
                    filterOption :
                    (formData.visibleToEmployees ? FILTER_NOTICE.EMPLOYEE : FILTER_NOTICE.CLIENT);
                handleChangedFilterByUserTypeAndScheduleStatus(newFilterOption, isNoticeActive);

                // Re-fetch both Noticeboards in case an edit added or removed an audience.
                fetchData(FILTER_NOTICE.EMPLOYEE);
                fetchData(FILTER_NOTICE.CLIENT);

                // Change the active filter if the updated notice now doesn't belong in the current one
                handleChangedFilterByUserTypeAndScheduleStatus(newFilterOption, isNoticeActive);
            } else {
                setFeedRefreshKey(current => current + 1);
            }

            setShowNoticeModal(false);
        } catch (error) {
            onSubmitError(error, modalSetErrors);
        } finally {
            setIsFormLoading(false);
        }
    };

    /**
     * @param formData
     * @param {NoticeImage[]|NoticeVideo[]|undefined} noticeMediaItem
     * @param modalSetErrors
     * @param attachedFiles
     * @param deleteAttachedFiles
     */
    const createNotice = async (formData, noticeMediaItem, modalSetErrors, attachedFiles, deleteAttachedFiles) => {
        if (noticeMediaItem && formData.postType !== POST_TYPE.CAROUSEL) {
            formData.imageUrl = noticeMediaItem[0].getMediaAssetUrl();
        }

        let formRequest = {
            ...formData
        };

        const noticeMediaItems = noticeMediaItem || null;

        const notice = await postNewNotice(formRequest, noticeMediaItems);
        await uploadRelatedMedia(notice.uuid, attachedFiles, deleteAttachedFiles);
        return notice;
    };

    /**
     * @param formData
     * @param {NoticeImage[]|NoticeVideo[]|undefined} noticeMediaItem
     * @param modalSetErrors
     * @param attachedFiles
     * @param deleteAttachedFiles
     */
    const updateNotice = async (formData, noticeMediaItem, modalSetErrors, attachedFiles, deleteAttachedFiles) => {
        let imageFile = null;
        if (noticeMediaItem && formData.postType !== POST_TYPE.CAROUSEL) {
            formData.imageUrl = noticeMediaItem[0].getMediaAssetUrl();
            if (noticeMediaItem.length <= 1) imageFile = noticeMediaItem[0]?.pendingLocalUpload() || null;
        }

        const noticeMediaItems = [
            ...(noticeMediaItem || [])
        ];

        // Update Notice
        let formRequest = {
            ...formData,
            "uuid": noticeData.uuid,
            "mediaHasChanged": imageFile !== null,
            "scheduleDate": formatDateToYmdTHi(formData.scheduleDate)
        };

        requireReaderTypes(formRequest.readerTypes);
        const readerType = getSingleReaderType(formRequest.readerTypes);
        const response = await axios.put(
            `${API_URL}/noticeboard/${noticeData.uuid}`,
            buildAppNoticeRequest(formRequest, noticeMediaItems),
            {
                headers: {
                    Authorization: "Bearer " + user.token
                }
            }
        );

        let updatedNotice = getRequiredResponseField(response, "announcement");
        let carouselS3PresignedUrls = response.data.s3PresignedUrls;

        if (formRequest.postType !== POST_TYPE.CAROUSEL && imageFile === null) {
            await uploadRelatedMedia(updatedNotice.uuid, attachedFiles, deleteAttachedFiles);
            return updatedNotice;
        }

        if (formRequest.postType === POST_TYPE.CAROUSEL) {
            let carouselMedia;
            if (noticeMediaItems && noticeMediaItems.length > 0) {
                const carouselMedia = await Promise.all(
                    noticeMediaItems.map(async (item) => {
                        let url = "";
                        const isEdited = item?.localDataBlob !== null;

                        if (isEdited) {
                            const file = item?.pendingLocalUpload() || null;
                            const fullUrl = carouselS3PresignedUrls.shift();
                            url = await uploadToS3(fullUrl, file);
                        } else {
                            url = item.urlSource;
                        }

                        const baseMedia = {
                            mediaType: item.mediaType(),
                            aspectRatio: item.aspectRatio,
                            url,
                        };
                 
                        if (item instanceof NoticeVideo) {
                            baseMedia.trimStart = item.trimStart ?? 0;
                            baseMedia.trimEnd = item.trimEnd ?? 0;
                            baseMedia.crop = {
                                x: item.cropping?.x ?? 0,
                                y: item.cropping?.y ?? 0,
                                width: item.cropping?.width ?? 0,
                                height: item.cropping?.height ?? 0,
                            };
                        }

                        // Preserve UUID if unedited
                        if (!isEdited && item.uuid) {
                            baseMedia.uuid = item.uuid;
                        }

                        return baseMedia;
                    })
                );
                formRequest.carouselMedia = carouselMedia;
                updatedNotice = await putNoticeboard(formRequest, { readerType });
            } else {
                carouselMedia = noticeData.carouselMedia || [];
                formRequest.carouselMedia = carouselMedia;
                updatedNotice = await putNoticeboard(formRequest, { readerType });
            }
        } else {
            if (formRequest.postType === POST_TYPE.VIDEO) {
                if (imageFile !== null) {
                    const fullUrl = response.data.s3PresignedUrl;
                    formRequest.url = await uploadToS3(fullUrl, imageFile);
                } else {
                    formRequest.url = formData.imageUrl || noticeData.video?.videoUrl || "";
                    formRequest.aspectRatio = noticeData.video?.aspectRatio || noticeData.carousel[0].aspectRatio;
                }
            } else {
                if (imageFile !== null) {
                    const fullUrl = response.data.s3PresignedUrl;
                    formRequest.url = await uploadToS3(fullUrl, imageFile);
                } else {
                    formRequest.url = formData.imageUrl || noticeData.imageUrl || "";
                }
            }
            updatedNotice = await putNoticeboard(formRequest, { readerType });
        }

        await uploadRelatedMedia(updatedNotice.uuid, attachedFiles, deleteAttachedFiles);

        return updatedNotice;
    };

    function onSubmitError(error, modalSetErrors) {
        const formErrors = error.getFormErrors();
        if (error.hasFormErrors()) {
            formErrors.forEach(({ field, message }) => {
                switch (field) {
                    case "subjectInvalid":
                        updateMessage("SUBJECT_INVALID", message);
                        return;
                    case "messageInvalid":
                        updateMessage("MESSAGE_INVALID", message);
                        return;
                    case "scheduleDateInvalid":
                        updateMessage("SCHEDULE_INVALID", message);
                        return;
                    default:
                        return;
                }
            });
            modalSetErrors([]);
            return;
        }

        Toast.error(error.getApiErrorDetails().message);
    }

    async function uploadRelatedMedia(noticeUuid, attachedFiles, deleteAttachedFiles) {
        if (attachedFiles) {
            for (const attachedFile of attachedFiles) {
                await uploadAttachedFileToS3AndSave(noticeUuid, attachedFile);
            }
        }

        if (deleteAttachedFiles) {
            for (const deleteAttachedFileObj of deleteAttachedFiles) {
                await deleteAttachedFile(noticeUuid, deleteAttachedFileObj);
            }
        }
    }

    async function uploadAttachmentToS3(fullUrl, filePreview, fileType) {
        await axios.put(fullUrl, filePreview, {
            headers: { "Content-Type": fileType }
        });

        const storedUrl = fullUrl.substring(0, fullUrl.indexOf("?"));
        return storedUrl;
    }

    async function postToSocialPlatform(noticeUuid, selectedSocials) {
        const data = {
            "selectedSocialsToPostUuids": selectedSocials
        };

        return await axios.post(`${API_URL}/company/noticeboard/${noticeUuid}/sendToSocialPlatform`,
            data,
            {
                headers: { Authorization: "Bearer " + user.token }
            }
        );
    }

    async function updatePostToSocialPlatform(noticeUuid, selectedSocials, socialsToDelete) {
        const data = {
            "selectedSocialsToPostUuids": selectedSocials,
            "socialsToDelete": socialsToDelete
        };

        return await axios.post(`${API_URL}/company/noticeboard/${noticeUuid}/updateDetailsToSocialPlatform`,
            data,
            {
                headers: { Authorization: "Bearer " + user.token }
            }
        );
    }

    async function uploadToS3(fullUrl, imageFile) {
        const storedUrl = fullUrl.substring(0, fullUrl.indexOf("?"));
        await axios.put(fullUrl, imageFile, {
            headers: { "Content-Type": imageFile.type }
        });
        return storedUrl;
    }

    const postNewNotice = async (requestData, noticeMediaItem) => {
        requireReaderTypes(requestData.readerTypes);
        const readerType = getSingleReaderType(requestData.readerTypes);
        const newNoticeResponse = await axios.post(
            `${API_URL}/noticeboard`,
            buildAppNoticeRequest(requestData, noticeMediaItem),
            {
                headers: {
                    Authorization: "Bearer " + user.token
                }
            }
        );
        let notice = getRequiredResponseField(newNoticeResponse, "announcement");
        let carouselS3PresignedUrls = newNoticeResponse.data.s3PresignedUrls;
        if (noticeMediaItem) {
            if (requestData.postType === POST_TYPE.CAROUSEL) {
                requestData.uuid = notice.uuid;
                if (requestData.carouselMedia && requestData.carouselMedia.length > 0) {
                    const updatedCarouselMedia = await Promise.all(
                        requestData.carouselMedia.map(async (mediaItem, index) => {
                            const file = noticeMediaItem[index]?.pendingLocalUpload() || null;
                            const fullUrl = carouselS3PresignedUrls[index];
                            const url = await uploadToS3(fullUrl, file);

                            return {
                                ...mediaItem,
                                url
                            };
                        })
                    );

                    requestData.carouselMedia = updatedCarouselMedia;
                } else {
                    const carouselMedia = await Promise.all(
                        noticeMediaItem.map(async (item, index) => {
                            const file = item.pendingLocalUpload() || null;
                            const fullUrl = carouselS3PresignedUrls[index];
                            const url = await uploadToS3(fullUrl, file);

                            const baseMedia = {
                                mediaType: item.mediaType(),
                                aspectRatio: item.aspectRatio,
                                url,
                            };
                            
                            if (item instanceof NoticeVideo) {
                                baseMedia.trimStart = item.trimStart ?? 0;
                                baseMedia.trimEnd = item.trimEnd ?? 0;
                                baseMedia.crop = {
                                    x: item.cropping?.x ?? 0,
                                    y: item.cropping?.y ?? 0,
                                    width: item.cropping?.width ?? 0,
                                    height: item.cropping?.height ?? 0,
                                };
                            }
                            
                            return baseMedia;
                        })
                    );

                    requestData.carouselMedia = carouselMedia;
                }

                notice = await putNoticeboard(requestData, { readerType });

            } else {
                const imageFile = noticeMediaItem[0]?.pendingLocalUpload() || null;
                const fullUrl = newNoticeResponse.data.s3PresignedUrl;
                requestData.uuid = notice.uuid;
                requestData.url = await uploadToS3(fullUrl, imageFile);
                notice = await putNoticeboard(requestData, { readerType });
            }
        }
        return notice;
    };

    async function putNoticeboard(formRequest, { readerType = null } = {}) {
        const mediaUpdateRequest = removeUndefinedFields({
            uuid: formRequest.uuid,
            url: formRequest.url,
            subject: formRequest.subject,
            message: formRequest.message,
            richTextMessage: formRequest.richTextMessage,
            postType: formRequest.postType,
            aspectRatio: formRequest.aspectRatio,
            readerType,
            readerTypes: formRequest.readerTypes,
            carouselMedia: formRequest.carouselMedia,
            crop: formRequest.crop,
            trimStart: formRequest.trimStart,
            trimEnd: formRequest.trimEnd
        });
        const responsePutCompanyNoticeboard = await axios.put(
            `${API_URL}/noticeboard`,
            mediaUpdateRequest,
            {
                headers: {
                    Authorization: "Bearer " + user.token
                }
            }
        );

        return getRequiredResponseField(responsePutCompanyNoticeboard, "announcement");
    }

    async function attachmentCreate(noticeUuid, formRequest) {
        const responsePutCompanyNoticeboard = await axios.post(`${API_URL}/noticeboard/attachment/${noticeUuid}`, formRequest, {
            headers: {
                Authorization: "Bearer " + user.token
            }
        });

        return responsePutCompanyNoticeboard;
    }

    const toggleShowAccountLinkingPage = () => {
        setShowAccountLinkingPage(!showAccountLinkingPage);
    };

    const fetchVideoByUuid = useCallback(async (videoUuid) => {
        try {
            const res = await axios.get(`${API_URL}/noticeboard/video/${videoUuid}`, {
                headers: { Authorization: `Bearer ${user.token}` }
            });

            const updatedVideo = res.data.data;

            const updateNoticeArray = (notices) => {
                return notices.map(notice => {
                    if (notice.video?.uuid === videoUuid) {
                        return {
                            ...notice,
                            video: { ...notice.video, ...updatedVideo },
                            pendingMedia: updatedVideo.pendingMedia,
                            videoUrl: updatedVideo.videoUrl
                        };
                    }

                    if (notice.postType === "carousel" && Array.isArray(notice.carousel)) {
                        const updatedItems = notice.carousel.map(item =>
                            item.uuid === videoUuid
                                ? { ...item, ...updatedVideo, pendingMedia: updatedVideo.pendingMedia }
                                : item
                        );

                        return {
                            ...notice,
                            carousel: updatedItems
                        };
                    }

                    return notice;
                });
            };

            setEmployeesArray(prev => updateNoticeArray(prev));
            setClientsArray(prev => updateNoticeArray(prev));
        } catch (error) {
            console.error("Error fetching video by UUID:", error);
        }
    }, [user.token]);

    function usePendingMediaPolling(fetchVideoByUuid) {
        const intervalRef = useRef(null);
        const noticesRef = useRef([]);
    
        const setNotices = (notices) => {
            noticesRef.current = notices;
        };

        const startPolling = () => {
            if (intervalRef.current) return;

            const getAllPendingVideoUuids = () => {
                const uuids = new Set();

                for (const notice of noticesRef.current) {
                    if (notice.postType === "video" && notice.pendingMedia && notice.video?.uuid) {
                        uuids.add(notice.video.uuid);
                    }

                    if (notice.postType === "carousel" && Array.isArray(notice.carousel)) {
                        for (const item of notice.carousel) {
                            if (item?.pendingMedia && item?.uuid) {
                                uuids.add(item.uuid);
                            }
                        }
                    }
                }

                return Array.from(uuids);
            };

            const pollPendingVideos = () => {
                const pendingUuids = getAllPendingVideoUuids();

                if (pendingUuids.length === 0) {
                    clearInterval(intervalRef.current);
                    intervalRef.current = null;
                    return;
                }

                pendingUuids.forEach(fetchVideoByUuid);
            };

            pollPendingVideos();
            intervalRef.current = setInterval(pollPendingVideos, 5000);
        };

        const stopPolling = () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };

        useEffect(() => {
            return () => stopPolling();
        }, []);

        return { setNotices, startPolling, stopPolling };
    }

    const { setNotices, startPolling } = usePendingMediaPolling(fetchVideoByUuid);

    const allNotices = useMemo(() => {
        return [...employeesArray, ...clientsArray];
    }, [employeesArray, clientsArray]);
    const yearOptions = useMemo(() => {
        const currentYear = new Date().getFullYear();
        return Array.from({ length: 7 }, (_, index) => currentYear - 3 + index);
    }, []);
    const activeNoticeFilterCount = useMemo(
        () => getActiveNoticeFilterCount(appliedNoticeFilters, canUseCheckedIn),
        [appliedNoticeFilters, canUseCheckedIn]
    );
    const activePostCount = allNotices.filter(notice => notice.isActive).length;
    const scheduledPostCount = allNotices.length - activePostCount;
    const openFeedView = () => {
        setShowAccountLinkingPage(false);
        setActiveView(NOTICEBOARD_VIEW.FEED);
    };
    const openManagementView = () => {
        if (!canManageNoticeboard) {
            return;
        }

        setActiveView(NOTICEBOARD_VIEW.MANAGE);
    };
    const selectedAudienceName = filterOption === FILTER_NOTICE.EMPLOYEE ?
        capitalizeString(pluralize.singular(customNames.employees)) :
        capitalizeString(pluralize.singular(customNames.clients));
    const pageTitle = activeView === NOTICEBOARD_VIEW.FEED ?
        "Noticeboard Feed" :
        `${selectedAudienceName} Noticeboard`;

    useEffect(() => {
        setNotices(allNotices);

        const hasPending = allNotices.some(notice =>
            (notice.postType === "video" && notice.pendingMedia) ||
        (notice.postType === "carousel" && Array.isArray(notice.carousel) && notice.carousel.some(item => item.pendingMedia))
        );

        if (hasPending) {
            startPolling();
        }
    }, [allNotices, setNotices, startPolling]);

    return (
        <>
            {activeView === NOTICEBOARD_VIEW.MANAGE && <FacebookSDKLoader />}
            <PortalPageShell>
                {activeView === NOTICEBOARD_VIEW.MANAGE && (
                    <BusinessPortalHeader
                        title={pageTitle}
                        description={`Recognise achievements, share updates, and publish images or videos to ${pluralize(customNames.employees.toLowerCase())} and ${pluralize(customNames.clients.toLowerCase())} from one communication hub.`}
                    >
                        {hasSocialMediaLinkingRole &&
                            <NoticeboardAccountLinkingCard toggleShowAccountLinkingPage={toggleShowAccountLinkingPage} />
                        }
                    </BusinessPortalHeader>
                )}

                {activeView === NOTICEBOARD_VIEW.MANAGE &&
                !showAccountLinkingPage && !connectingLinkedInAccount && (
                    <PortalSurface padding="1rem">
                        <PortalMetricGrid columns={5}>
                            <PortalMetricCard>
                                <PortalMetricLabel>{capitalizeString(pluralize(customNames.employees))} posts</PortalMetricLabel>
                                <PortalMetricValue>{employeesArray.length}</PortalMetricValue>
                            </PortalMetricCard>
                            <PortalMetricCard>
                                <PortalMetricLabel>{capitalizeString(pluralize(customNames.clients))} posts</PortalMetricLabel>
                                <PortalMetricValue>{clientsArray.length}</PortalMetricValue>
                            </PortalMetricCard>
                            <PortalHighlightMetricCard>
                                <PortalMetricLabel>Active posts</PortalMetricLabel>
                                <PortalMetricValue>{activePostCount}</PortalMetricValue>
                            </PortalHighlightMetricCard>
                            <PortalMetricCard>
                                <PortalMetricLabel>Scheduled posts</PortalMetricLabel>
                                <PortalMetricValue>{scheduledPostCount}</PortalMetricValue>
                            </PortalMetricCard>
                            <NoticeboardViewCard>
                                <div>
                                    <NoticeboardViewCardTitle>View Noticeboard Feed</NoticeboardViewCardTitle>
                                    <NoticeboardViewCardText>
                                        Browse published posts as they appear to employees and clients.
                                    </NoticeboardViewCardText>
                                </div>
                                <NoticeboardViewCardButton type="button" onClick={openFeedView}>
                                    Open Feed View
                                </NoticeboardViewCardButton>
                            </NoticeboardViewCard>
                        </PortalMetricGrid>
                        <PortalCaption>
                            Use the filters below to move between audiences and scheduled publishing states.
                        </PortalCaption>
                    </PortalSurface>
                )}

                {activeView === NOTICEBOARD_VIEW.MANAGE && showAccountLinkingPage &&
                    <AccountLinkingPage
                        user={user}
                        toggleShowAccountLinkingPage={toggleShowAccountLinkingPage}
                        retrieveLinkedAccounts={retrieveLinkedAccounts}
                        saveFacebookDetails={saveFacebookDetails}
                        saveInstagramDetails={saveInstagramDetails}
                        revokeSocialMediaAccount={revokeSocialMediaAccount}
                        handleFacebookReconnect={handleFacebookReconnect}
                        handleInstagramReconnect={handleInstagramReconnect}
                        linkedAccounts={linkedAccounts}
                        linkedAccountsFetched={linkedAccountsFetched}
                        showAccountsLinked={showAccountsLinked}
                        connectingLinkedInAccount={connectingLinkedInAccount}
                        setConnectingLinkedInAccount={setConnectingLinkedInAccount}
                    />
                }

                {activeView === NOTICEBOARD_VIEW.MANAGE &&
                !showAccountLinkingPage && !connectingLinkedInAccount &&
                    <NoticeboardTable
                        user={user}
                        usersArray={usersArray}
                        handleNewNotice={handleNewNotice}
                        handleEditNotice={handleEditNotice}
                        requestDeleteNoticesConfirmation={requestDeleteNoticesConfirmation}
                        handleFilterChangeByUserType={handleFilterChangeByUserType}
                        employeesCounter={employeesArray.length}
                        clientsCounter={clientsArray.length}
                        checkedNotices={checkedNotices}
                        handleCheckboxChange={handleCheckboxChange}
                        handleCheckAllNotices={handleCheckAllNotices}
                        checkAllNotices={checkAllNotices}
                        isFetching={isFetching}
                        filterByUserTypeOption={filterOption}
                        filterByOption={filterByOption}
                        handleFilterByChange={handleFilterByChange}
                        handleOpenNoticeFilters={handleOpenNoticeFilters}
                        activeNoticeFilterCount={activeNoticeFilterCount}
                        handleShowNoticeViewershipModal={handleShowNoticeViewershipModal}
                    />
                }

                {activeView === NOTICEBOARD_VIEW.FEED && (
                    <NoticeboardFeedView
                        user={user}
                        canManage={canManageNoticeboard}
                        canCreateClientPost={canCreateClientPost}
                        canUseCheckedIn={canUseCheckedIn}
                        onCreateClientPost={handleNewNotice}
                        onOpenManagement={openManagementView}
                        refreshKey={feedRefreshKey}
                    />
                )}
            </PortalPageShell>

            {activeView === NOTICEBOARD_VIEW.MANAGE && showDeleteDialog &&
                <DeleteDialog
                    showDeleteDialog={showDeleteDialog}
                    handleCloseDeleteDialog={handleCloseDeleteDialog}
                    handleDelete={handleDelete}
                    isMultipleDelete={checkedNotices.length > 1}
                    isRequestingDeletion={isRequestingDeletion}
                />
            }

            {(activeView === NOTICEBOARD_VIEW.MANAGE || canCreateClientPost) && showNoticeModal &&
                <NoticeModal
                    user={user}
                    showNoticeModal={showNoticeModal}
                    isEditForm={showNoticeModal && noticeData !== null}
                    handleCloseModal={handleCloseNoticeModal}
                    noticeData={noticeData && { ...noticeData, scheduleDate: moment.utc(noticeData.scheduleDateUtc).local().format("YYYY-MM-DDTHH:mm") }}
                    filterOption={filterOption}
                    createOrUpdateNotice={createOrUpdateNotice}
                    isFormLoading={isFormLoading}
                    handleDelete={handleDelete}
                    requestDeleteNoticesConfirmation={requestDeleteNoticesConfirmation}
                    responseMessage={responseMessage}
                    emptyResponseMessage={() => { setResponseMessage(DEFAULT_RESPONSE_MESSAGE); }}
                    retrieveLinkedAccounts={retrieveLinkedAccounts}
                    linkedAccounts={linkedAccounts}
                    linkedAccountsFetched={linkedAccountsFetched}
                    hasSocialMediaPostingRole={!isClientUser && hasSocialMediaPostingRole}
                    canUseCheckedIn={canUseCheckedIn}
                    openLinkedAccounts={handleOpenLinkedAccountsFromNoticeModal}
                    lockToClientAudience={isClientUser}
                />
            }

            {activeView === NOTICEBOARD_VIEW.MANAGE && showNoticeViewershipModal &&
                <NoticeViewershipModal
                    user={user}
                    noticeData={noticeData}
                    showNoticeViewershipModal={showNoticeViewershipModal}
                    handleCloseNoticeViewershipModal={handleCloseNoticeViewershipModal}
                />
            }

            {activeView === NOTICEBOARD_VIEW.MANAGE && (
                <NoticeboardFiltersModal
                    open={showNoticeFilters}
                    filters={draftNoticeFilters}
                    canUseCheckedIn={canUseCheckedIn}
                    yearOptions={yearOptions}
                    onClose={() => setShowNoticeFilters(false)}
                    onChange={setDraftNoticeFilters}
                    onToggleCategory={toggleDraftNoticeCategory}
                    onApply={handleApplyNoticeFilters}
                    onClear={handleClearNoticeFilters}
                />
            )}

            {canManageNoticeboard && activeView === NOTICEBOARD_VIEW.MANAGE && isFinalisingLinkedIn &&
                <FinaliseLinkedInModal
                    code={code}
                    setConnectingLinkedInAccount={setConnectingLinkedInAccount}
                    queryAvailableLinkedInOrganisations={queryAvailableLinkedInOrganisations}
                    saveLinkedInDetails={saveLinkedInDetails}
                    dismissLinkedInLinking={dismissLinkedInLinking}
                />
            }
        </>
    );
};

export default withRouter(Noticeboard);
